// Shared data-table primitives + status badge, so every table reads identically:
// sticky headers, tabular numerals for money, consistent padding and empty/loading states.

const TONES = {
  paid: "bg-green-50 text-agri-forest-light",
  active: "bg-green-50 text-agri-forest-light",
  "in stock": "bg-green-50 text-agri-forest-light",
  in: "bg-green-50 text-agri-forest-light",
  partial: "bg-amber-50 text-agri-amber-dark",
  low: "bg-amber-50 text-agri-amber-dark",
  pending: "bg-amber-50 text-agri-amber-dark",
  sale: "bg-amber-50 text-agri-amber-dark",
  adjustment: "bg-blue-50 text-blue-700",
  credit: "bg-red-50 text-agri-danger",
  blacklisted: "bg-red-50 text-agri-danger",
  out: "bg-red-50 text-agri-danger",
};

export function StatusBadge({ status, label }) {
  const key = String(status ?? "").toLowerCase();
  const tone = TONES[key] || "bg-black/5 text-agri-ink-2";
  return (
    <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded-full capitalize ${tone}`}>
      {label ?? status}
    </span>
  );
}

// Column / cell classes — compose in <th>/<td>.
export const theadCls = "sticky top-0 z-10 bg-white/95 backdrop-blur border-b border-agri-ink/10 text-[11px] uppercase tracking-wider text-agri-ink-2/70";
export const thCls = "px-4 py-3 text-left font-semibold whitespace-nowrap";
export const thNumCls = "px-4 py-3 text-right font-semibold whitespace-nowrap";
export const tdCls = "px-4 py-3.5 align-middle";
export const tdNumCls = "px-4 py-3.5 align-middle text-right tabular-nums";
export const rowCls = "border-b border-agri-ink/[0.06] hover:bg-agri-bg/40 transition-colors";

// Card wrapper with horizontal + vertical scroll; keeps the header sticky.
export function TableCard({ children, className = "", testId }) {
  return (
    <div className={`sm:card-soft sm:overflow-hidden ${className}`}>
      {/* Desktop: scroll box with sticky header. Mobile: rows become cards and flow with the page. */}
      <div className="overflow-visible sm:overflow-auto sm:max-h-[70vh]">
        <table className="w-full text-sm rtable" data-testid={testId}>{children}</table>
      </div>
    </div>
  );
}

export function EmptyRow({ colSpan, icon: Icon, children }) {
  return (
    <tr>
      <td colSpan={colSpan} className="px-4 py-12 text-center rt-full">
        <div className="flex flex-col items-center gap-2 text-agri-ink-2">
          {Icon && <Icon size={26} className="opacity-40" />}
          <div className="text-sm">{children}</div>
        </div>
      </td>
    </tr>
  );
}

export function SkeletonRows({ rows = 6, cols }) {
  return Array.from({ length: rows }).map((_, r) => (
    <tr key={`sk-${r}`} className="border-t border-[rgba(28,28,30,0.05)]">
      {Array.from({ length: cols }).map((_, c) => (
        <td key={c} className="px-4 py-3 rt-full">
          <div
            className="h-3.5 rounded bg-agri-ink/[0.06] animate-pulse"
            style={{ width: c === 0 ? "70%" : "45%" }}
          />
        </td>
      ))}
    </tr>
  ));
}
